// eslint-disable-next-line max-classes-per-file
import _ from "lodash";
import React from "react";
import {dateSort, durationSort, sortValues} from "./sortValues";
import fieldFilters from "./fieldFilters";
import fieldFormatter from "./fieldFormatter";
import DrillDownLink from "../components/DrillDownLink";
import ExternalLink from "../../report/common/components/ExternalLink";
import ReportNavigationButton from "../../report/common/components/ReportNavigationButton";
import config from "../../../core/config/configureSettings";

class Column {
  constructor(header, accessor, sortable = true) {
    this.Header = header;
    this.accessor = accessor;
    this.sortable = sortable;
    this.excelFormatter = undefined; // Excel-specific formatter name
  }

  headerClassName = "wordwrap";

  withType(type) {
    this.type = type;
    return this;
  }

  withExcelFormatter(formatterName) {
    this.excelFormatter = formatterName;
    return this;
  }

  withFormatter(webFormatter, excelFormatter = null) {
    this.Cell =
      typeof webFormatter === "string"
        ? fieldFormatter(webFormatter)
        : webFormatter;
    this.excelFormatter =
      excelFormatter ||
      (typeof webFormatter === "string" ? webFormatter : null);
    return this;
  }

  withWidth(width) {
    this.width = width;
    return this;
  }

  withMinWidth(width) {
    this.minWidth = width;
    return this;
  }

  withId(id) {
    this.id = id;
    return this;
  }

  withTotalHeader(totalHeader, className = "") {
    this.Footer = totalHeader;
    this.getFooterProps = () => ({ className });
    return this;
  }

  withTotalHeaderEpg(
    totalHeader,
    className = "",
    totalHeaderSecond,
    data,
    index
  ) {
    let columnColor = "white";
    let textColor = "grey";
    if (data) {
      if (data.pctPerformance <= 100 && data.pctPerformance >= 97) {
        columnColor = "#00AF4F";
        textColor = "white";
      } else if (data.pctPerformance <= 96.9 && data.pctPerformance >= 95) {
        columnColor = "#ffac31";
        textColor = "white";
      } else if (data.pctPerformance <= 95 && data.pctPerformance !== null) {
        columnColor = "#D20020";
        textColor = "white";
      }
    }
    if (data) {
      if (
        data.monthYears[index] <= 100 &&
        data.monthYears[index] >= 97 &&
        data.pctPerformance !== null
      ) {
        columnColor = "#00AF4F";
        textColor = "white";
      } else if (
        data.monthYears[index] <= 96.9 &&
        data.monthYears[index] >= 95 &&
        data.pctPerformance !== null
      ) {
        columnColor = "#ffac31";
        textColor = "#6A6B6B";
      } else if (
        (data.monthYears[index] <= 95 &&
          data.monthYears[index] !== null &&
          data.pctPerformance !== null) ||
        data.monthYears[index] === 0.0
      ) {
        columnColor = "#D20020";
        textColor = "white";
      }
    }
    this.Footer = (
      <div>
        <div style={{ backgroundColor: "#BFBFBF", padding: "0 5px" }}>
          {totalHeader}
        </div>
        <div
          style={{
            backgroundColor: columnColor,
            color: textColor,
            padding: "0 5px",
          }}
        >
          {totalHeaderSecond}
        </div>
      </div>
    );
    this.getFooterProps = () => ({ className });
    return this;
  }

  withShow(show) {
    this.show = show;
    return this;
  }

  withGetProps(getProps) {
    this.getProps = getProps;
    return this;
  }

  withExpandable(className = "") {
    this.getProps = (state, rowInfo) => ({
      className:
        className + (state.expanded[rowInfo.viewIndex] ? "" : " collapsed"),
    });
    return this;
  }
}

export default (data) => {
  const fieldFiltersWithData = fieldFilters(data);

  class BooleanColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter("boolean");
      this.excelFormatter = "boolean";
      Object.assign(this, fieldFiltersWithData.applyBooleanFilter(this));
    }
  }

  class DateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDateFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("date");
      this.excelFormatter = "date";
    }
  }

  class DateTimeColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("dateTime");
      this.excelFormatter = "dateTime";
    }
  }

  class DateTimeColumnWithUtc extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("dateTimeWithUtc");
      this.excelFormatter = "dateTimeWithUtc";
      this.type = "dateTimeWithUtc";
    }
  }

  class DateTimeColumnwithSecond extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("dateTimeWithSecond");
      this.excelFormatter = "dateTimeWithSecond";
    }
  }

  class ColoredDateTimeColumn extends Column {
    constructor(header, accessor, sortable, headerClassName) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("dateTime");
      this.excelFormatter = "dateTime";
      this.headerClassName = headerClassName;
    }
  }

  class TextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
    }
  }

  class AmberColorTextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.Cell = (row) => {
        if (row.value === "*") {
          this.headerClassName = "bg-header-orange-amber";
        }
        return row.value;
      };
    }
  }

  class TextWrapColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.className = "wordwrap";
    }
  }

  class NonZeroTextColumn extends TextColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter("nonZeroText");
      this.excelFormatter = "text";
    }
  }

  class MultilineColumn extends TextColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyMultilineTextFilter(this));
      this.Cell = fieldFormatter("multiline");
      this.excelFormatter = "text";
    }
  }

  class MultilineNumberColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("multilineNumber");
      this.excelFormatter = "numberNumber";
      this.className = "-column-align-right";
    }
  }

  class MultilinePercentageColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("multilinePercentage");
      this.excelFormatter = "percentageWithoutSign";
      this.className = "-column-align-right";
    }
  }

  class MultilineDateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDateFilter(this));
      this.Cell = fieldFormatter("multilineDate");
      this.excelFormatter = "date";
    }
  }

  class MultilineDateTimeColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
      this.sortMethod = dateSort;
      this.Cell = fieldFormatter("multilineDateTime");
      this.excelFormatter = "dateTime";
    }
  }

  class MultilineExternalReceptacleLinkColumn extends TextColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyMultilineTextFilter(this));
      this.Cell = fieldFormatter("multilineReceptacleLink");
      this.excelFormatter = "text";
    }
  }

  class DurationColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.Cell = fieldFormatter("duration");
      this.excelFormatter = "text";
    }
  }

  class DurationColumnWithDay extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
      this.Cell = fieldFormatter("durationWithDay");
      this.excelFormatter = "text";
      this.sortMethod = durationSort;
      this.className = "-column-align-right";
    }
  }

  class NumberColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
      this.sortMethod = sortValues;
      this.className = "-column-align-right";
      this.excelFormatter = "numberNumber";
    }
  }

  class NonZeroNumberColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter("nonZeroNumber");
      this.excelFormatter = "numberNumber";
    }
  }

  class NonZeroNumberSecondColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter("nonZeroNumberDurationSecond");
      this.excelFormatter = "numberNumber";
    }
  }

  class FloatColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyFloatFilter(this));
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("float");
      this.excelFormatter = "numberNumber";
      this.className = "-column-align-right";
    }
  }

  class NonZeroFloatColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyFloatFilter(this));
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("nonZeroFloat");
      this.excelFormatter = "numberNumber";
      this.className = "-column-align-right";
    }
  }

  class PercentageColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
      // Web: Shows "75.5%" with % sign
      this.Cell = fieldFormatter("percentage");
      // Excel: Returns 75.5 as Number (no % sign, no decimal conversion)
      this.excelFormatter = "percentageWithoutSign";
      this.sortMethod = sortValues;
      this.className = "-column-align-right";
    }
  }

  class NonZeroPercentageColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter("nonZeroPercentage");
      this.excelFormatter = "percentageWithoutSign";
    }
  }

  class DrillDownColumn {
    constructor(column) {
      Object.assign(this, column);
      this.originalColumn = column;
      this.originalCell = this.Cell;
      this.originalFooter = this.Footer;
      this.originalFilter = this.Filter;
      this.Cell = _.bind(DrillDownLink.cell, this, _, this.originalCell);
      this.Footer = _.bind(DrillDownLink.total, this, _, this.originalFooter);
      this.Filter = (props) =>
        this.originalFilter({
          ...props,
          column: {
            // eslint-disable-next-line react/prop-types
            ...props.column,
            Cell: this.originalColumn.Cell,
          },
        });
    }
  }

  class NavigationColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    // eslint-disable-next-line no-shadow
    total = (
      {
        // eslint-disable-next-line no-unused-vars,react/prop-types,no-shadow
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterPercentageColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    total = (
      {
        // eslint-disable-next-line no-shadow,react/prop-types,no-unused-vars
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("nonZeroPercentage");
        const value =
          formatter && row.value !== "-" ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterWithZeroPercentageColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    total = (
      {
        // eslint-disable-next-line no-shadow,no-unused-vars,react/prop-types
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("percentage");
        // eslint-disable-next-line no-nested-ternary
        const value =
          formatter && row.value !== "-"
            ? formatter(row) === "0.0 %"
              ? "-"
              : formatter(row)
            : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterMultipleDrilldownOptionColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    total = (
      {
        // eslint-disable-next-line no-shadow,no-unused-vars,react/prop-types
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
          header={column.Header}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterMultipleDrilldownOptionPercentageColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    total = (
      {
        // eslint-disable-next-line no-shadow,no-unused-vars,react/prop-types
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
          header={column.Header}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("nonZeroPercentage");
        const value =
          formatter && row.value !== "-" ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class NavigationWithFooterMultipleDrilldownOptionWithZeroPercentageColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    total = (
      {
        // eslint-disable-next-line no-shadow,no-unused-vars,react/prop-types
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
          header={column.Header}
        >
          {valueTotal}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("percentage");
        // eslint-disable-next-line no-nested-ternary
        const value =
          formatter && row.value !== "-"
            ? formatter(row) === "0.0 %"
              ? "-"
              : formatter(row)
            : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class PercentageNavigationColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("nonZeroPercentage");
        const value = formatter ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class PercentageZeroNavigationColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.Cell = (row, formatter) => {
        // eslint-disable-next-line no-param-reassign
        formatter = fieldFormatter("percentage");
        const value = formatter ? formatter(row) : row.value;
        if (value) {
          // eslint-disable-next-line react/no-this-in-sfc
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            reportId,
            mainReportId,
            requestParams,
            selectionCriteriaOptions,
            reportsToBeCleared,
            // eslint-disable-next-line react/no-this-in-sfc
          } = this.navOptionsCallback(row);
          return (
            <ReportNavigationButton
              reportId={reportId}
              mainReportId={mainReportId}
              requestParams={requestParams}
              selectionCriteriaOptions={selectionCriteriaOptions}
              reportsToBeCleared={reportsToBeCleared}
            >
              {value}
            </ReportNavigationButton>
          );
        }
        return "";
      };
    }
  }

  class MultilineNumberNavigationColumn {
    navOptionsCallback = () => ({});

    constructor(column, navOptionsCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.Cell = (row, formatter) => {
        if (formatter ? formatter(row) : row.value) {
          const values = row.value || [];
          return values.map((value, i) => {
            const {
              reportId,
              mainReportId,
              requestParams,
              selectionCriteriaOptions,
              reportsToBeCleared,
            } = this.navOptionsCallback(row, i);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div className="ellipsis-link multiline-item" key={i}>
                <ReportNavigationButton
                  reportId={reportId}
                  mainReportId={mainReportId}
                  requestParams={requestParams}
                  selectionCriteriaOptions={selectionCriteriaOptions}
                  reportsToBeCleared={reportsToBeCleared}
                >
                  {value}
                </ReportNavigationButton>
              </div>
            );
          });
        }
        return "";
      };
    }
  }

  class MultilineNumberNavigationWithFooterColumn {
    navOptionsCallback = () => ({});

    total = (
      {
        // eslint-disable-next-line no-shadow,no-unused-vars,react/prop-types
        data,
        column,
        row,
        index,
      },
      totalHeader
    ) => {
      const valueTotal =
        typeof totalHeader === "function"
          ? totalHeader({ column, data })
          : totalHeader;
      const {
        reportId,
        mainReportId,
        requestParams,
        selectionCriteriaOptions,
        reportsToBeCleared,
      } = column.parentColumn.drillDownLink;

      const displayedGrandTotalLinkValue = column.originalGrandTotal
        ? `${valueTotal}/${column.originalGrandTotal}`
        : valueTotal;
      return (
        <ReportNavigationButton
          reportId={reportId}
          mainReportId={mainReportId}
          requestParams={requestParams}
          selectionCriteriaOptions={selectionCriteriaOptions}
          reportsToBeCleared={reportsToBeCleared}
        >
          {displayedGrandTotalLinkValue}
        </ReportNavigationButton>
      );
    };

    constructor(column, navOptionsCallback) {
      Object.assign(this, column);
      Object.assign(
        this,
        fieldFiltersWithData.applyMultilineGroupedNumberFilter(this)
      );
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.originalFooter = this.Footer;
      this.Footer = _.bind(this.total, this, _, this.originalFooter);
      this.Cell = (row, formatter) => {
        if (formatter ? formatter(row) : row.value) {
          const values = row.value || [];
          return values.map((value, i) => {
            const {
              reportId,
              mainReportId,
              requestParams,
              selectionCriteriaOptions,
              reportsToBeCleared,
            } = this.navOptionsCallback(row, i);
            const displayedValue =
              i === 0 && row.original && row.original.originalGroupTotal
                ? `${value}/${row.original.originalGroupTotal}`
                : value;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div className="ellipsis-link multiline-item" key={i}>
                <ReportNavigationButton
                  reportId={reportId}
                  mainReportId={mainReportId}
                  requestParams={requestParams}
                  selectionCriteriaOptions={selectionCriteriaOptions}
                  reportsToBeCleared={reportsToBeCleared}
                >
                  {displayedValue}
                </ReportNavigationButton>
              </div>
            );
          });
        }
        return "";
      };
    }
  }

  class ToggleRowButtonColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.expander = true;
    }
  }

  class ExternalItemLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        const path = `/en/item/${value}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={value} path={path} />;
      };
    }
  }

  class ExternalDespatchLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        const path = `/en/despatch/${value}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={value} path={path} />;
      };
    }
  }

  class ExternalReceptacleLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        const path = `/en/receptacle/${value}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={value} path={path} />;
      };
    }
  }

  class ExternalConsignmentLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        const path = `/en/consignment/${value}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={value} path={path} />;
      };
    }
  }

  class ExternalReportDesatchMonitoringResponsePerformanceColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const { responsePerformanceRecord } = row.original;
        const atatlink = responsePerformanceRecord.despatchId;
        const value = formatter ? formatter(row) : atatlink;
        const path = `/en/despatch/${value}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={atatlink} path={path} />;
      };
    }
  }
  class ExternalCountryCodeConcatenatedConsignmentLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row, formatter) => {
        const value = formatter ? formatter(row) : row.value;
        const linkValue = row.original.consignmentId.countryCode + value;
        const path = `/en/consignment/${linkValue}`;
        const { atatSystem } = config.applications;
        const host = atatSystem;
        return <ExternalLink host={host} label={value} path={path} />;
      };
    }
  }

  class MultilineNumberGroupedColumn extends Column {
    constructor(header, accessor, sortable, peerPercentageColumnId) {
      super(header, accessor, sortable);
      Object.assign(
        this,
        fieldFiltersWithData.applyMultilineGroupedNumberFilter(this)
      );
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("multilineNumber");
      this.excelFormatter = "number";
      this.className = "-column-align-right";
      this.peerPercentageColumnId = peerPercentageColumnId;
    }

    countable = true;

    peerPercentageColumnId = "";
  }

  class MultilinePercentageGroupedColumn extends Column {
    constructor(header, accessor, sortable, peerCountableColumnId) {
      super(header, accessor, sortable);
      Object.assign(
        this,
        fieldFiltersWithData.applyMultilineGroupedNumberFilter(this)
      );
      this.sortMethod = sortValues;
      this.Cell = fieldFormatter("multilinePercentage");
      this.excelFormatter = "percentageWithoutSign";
      this.className = "-column-align-right";
      this.peerCountableColumnId = peerCountableColumnId;
    }

    percentage = true;

    peerCountableColumnId = "";
  }

  return {
    BooleanColumn,
    DateTimeColumn,
    ColoredDateTimeColumn,
    DateColumn,
    DateTimeColumnwithSecond,
    FloatColumn,
    TextColumn,
    AmberColorTextColumn,
    TextWrapColumn,
    ToggleRowButtonColumn,
    MultilineColumn,
    MultilineDateColumn,
    MultilineDateTimeColumn,
    MultilineExternalReceptacleLinkColumn,
    MultilineNumberColumn,
    MultilineNumberGroupedColumn,
    MultilinePercentageColumn,
    MultilinePercentageGroupedColumn,
    MultilineNumberNavigationColumn,
    DurationColumn,
    DurationColumnWithDay,
    NumberColumn,
    PercentageColumn,
    DrillDownColumn,
    NavigationColumn,
    ExternalItemLinkColumn,
    ExternalDespatchLinkColumn,
    ExternalReceptacleLinkColumn,
    ExternalConsignmentLinkColumn,
    ExternalCountryCodeConcatenatedConsignmentLinkColumn,
    NonZeroNumberColumn,
    NonZeroPercentageColumn,
    NonZeroFloatColumn,
    NonZeroTextColumn,
    PercentageNavigationColumn,
    PercentageZeroNavigationColumn,
    NavigationWithFooterColumn,
    NavigationWithFooterPercentageColumn,
    MultilineNumberNavigationWithFooterColumn,
    DateTimeColumnWithUtc,
    NavigationWithFooterMultipleDrilldownOptionColumn,
    NavigationWithFooterMultipleDrilldownOptionPercentageColumn,
    NonZeroNumberSecondColumn,
    ExternalReportDesatchMonitoringResponsePerformanceColumn,
    NavigationWithFooterMultipleDrilldownOptionWithZeroPercentageColumn,
    NavigationWithFooterWithZeroPercentageColumn,
  };
};
